import React, { useEffect, useState } from 'react'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useAPI } from '../../Contexts/APIContext'
import { ButtonOutline } from '../../Components/GeneralComponents/ButtonOutline/ButtonOutline'
import { ModalWindow } from '../../Components/GeneralComponents/ModalWindow/ModalWindow'
import { Breadcrumbs } from '../../Components/GeneralComponents/Breadcrumbs/Breadcrumbs'
import { StickyHeader } from '../../Components/StickyHeader'
import { Wrapper } from '../../Components/page/Wrapper'
import Loader from '../../Components/GeneralComponents/Loader'
import errorToast from '../../Components/Toasts/ErrorToast'
import { FilterDetail } from './FilterDetail'
import { ImportZipModal } from './ImportZipModal'
import {
  MAX_WEBP_BYTES,
  WEBP_REQUIREMENTS_COPY,
  deriveToken,
  toTitleCase,
  warnIfNotPortrait916,
} from './webpRequirements'
import classes from './index.module.css'

const SHOW_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'published', label: 'Published' },
  { value: 'draft', label: 'Draft' },
]

function statusLabel(filter) {
  if (filter.is_published) return 'published'
  return 'draft'
}

export function Filters() {
  const pageLimit = 50
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const {
    getOverlayFilters,
    createOverlayFilter,
    getUrlNewOverlayFilterMedia,
    uploadOverlayFilterMedia,
    finalizeOverlayFilterMedia,
  } = useAPI()

  const [show, setShow] = useState('all')
  const [selectedFilter, setSelectedFilter] = useState(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [isImportOpen, setIsImportOpen] = useState(false)

  // create-modal form state
  const [newName, setNewName] = useState('')
  const [newTags, setNewTags] = useState('')
  const [newMood, setNewMood] = useState('')
  const [newFile, setNewFile] = useState(null)
  // Finalized media held across re-submits: content-addressed, so a failed create
  // (e.g. duplicate token) can be retried without re-uploading the same asset.
  const [finalizedMedia, setFinalizedMedia] = useState(null)

  const derivedToken = deriveToken(newName)

  // Escape closes the create modal (but not while a create is in flight, matching
  // the onClose guard). ModalWindow has no key handling of its own.
  const closeCreate = () => {
    if (!isCreating) {
      setIsCreateOpen(false)
      resetCreateForm()
    }
  }
  useEffect(() => {
    if (!isCreateOpen) return
    const onKeyDown = e => {
      if (e.key === 'Escape') closeCreate()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  })

  // Server list supports show=all|published only. 'draft' is a client-side subset
  // of the full list (unpublished rows); deleted rows are never returned.
  const serverShow = show === 'published' ? 'published' : 'all'

  const { data, isError, error, isLoading } = useInfiniteQuery({
    queryKey: ['overlayFilters', serverShow],
    queryFn: async ({ pageParam = 1 }) => {
      const fetchAllPages = async (currentPage = 1, allFilters = []) => {
        const result = await getOverlayFilters(currentPage, pageLimit, serverShow)
        const rows = result.overlay_filters || result.filters || []
        const total = result.overlay_filters_count ?? result.count ?? rows.length
        const updated = [...allFilters, ...rows]
        if (rows.length > 0 && updated.length < total) {
          return fetchAllPages(currentPage + 1, updated)
        }
        return { ...result, overlay_filters: updated }
      }
      return fetchAllPages(pageParam)
    },
    getNextPageParam: () => undefined, // fetched all at once, matching Backdrop
  })

  const allFilters = data?.pages?.flatMap(page => page.overlay_filters) || []
  const filters =
    show === 'draft' ? allFilters.filter(f => !f.is_published) : allFilters

  const invalidateFilters = async () => {
    await queryClient.invalidateQueries({ queryKey: ['overlayFilters'] })
  }

  const resetCreateForm = () => {
    setNewName('')
    setNewTags('')
    setNewMood('')
    setNewFile(null)
    setFinalizedMedia(null)
  }

  const parseCsv = value =>
    value
      .split(',')
      .map(v => v.trim())
      .filter(Boolean)

  const handleFileChange = event => {
    const file = event.target.files?.[0]
    event.target.value = '' // allow re-selecting the same file
    if (!file) return
    if (file.type !== 'image/webp') {
      errorToast('Only animated WebP (.webp) files are allowed')
      return
    }
    if (file.size > MAX_WEBP_BYTES) {
      errorToast('File must be 10MB or smaller')
      return
    }
    setNewFile(file)
    // A new file invalidates any previously finalized media.
    setFinalizedMedia(null)
  }

  const handleCreate = async () => {
    // Force Title Case at submit (item 6: casing is applied on blur + here, not on
    // every keystroke) and derive the token from that final name.
    const finalName = toTitleCase(newName.trim())
    const finalToken = deriveToken(finalName)
    if (!finalName) {
      errorToast('Name is required')
      return
    }
    // Validate the token BEFORE any upload — deriveToken strips a leading digit run
    // to satisfy the backend's leading-letter rule, so an empty result means the
    // name has no usable letters and the create would dead-end after upload.
    if (!finalToken) {
      errorToast('Name must contain at least one letter')
      return
    }
    if (!newFile && !finalizedMedia) {
      errorToast('Choose an animated WebP file')
      return
    }
    setIsCreating(true)
    try {
      // Upload + finalize the asset first (unless a prior attempt already did and
      // we're just retrying the create). media_f_id is content-addressed, so
      // reusing it on retry is safe and skips a redundant upload.
      let media = finalizedMedia
      if (!media) {
        const { upload_id, upload_url } = await getUrlNewOverlayFilterMedia()
        await uploadOverlayFilterMedia(upload_url, newFile)
        // Server probes the WebP and derives dims/duration/frame_count; it may
        // reject (e.g. not an animated WebP).
        media = await finalizeOverlayFilterMedia(upload_id)
        setFinalizedMedia(media)
        warnIfNotPortrait916(media.width, media.height)
      }

      const created = await createOverlayFilter(
        finalToken,
        finalName,
        { tags: parseCsv(newTags), mood: parseCsv(newMood) },
        { media_f_id: media.media_f_id, format: 'animated_webp', loop: true }
      )
      await invalidateFilters()
      setIsCreateOpen(false)
      resetCreateForm()
      // open the new draft so the admin can preview / publish it
      if (created?.f_id) {
        setSelectedFilter(created)
      }
    } catch (e) {
      // Keep the modal open; finalizedMedia is retained so re-submit reuses it.
      errorToast(`Error: ${e.status ?? ''} ${e.message ?? ''}`)
    } finally {
      setIsCreating(false)
    }
  }

  const thumbnailUrl = filter => filter?.render_config?.sources?.[0]?.url

  return (
    <>
      {selectedFilter && (
        <FilterDetail
          filterFID={selectedFilter.f_id}
          onClose={() => setSelectedFilter(null)}
          onChanged={invalidateFilters}
        />
      )}

      {isImportOpen && (
        <ImportZipModal
          onClose={() => setIsImportOpen(false)}
          onDone={invalidateFilters}
        />
      )}

      <ModalWindow open={isCreateOpen} onClose={closeCreate}>
        <div className={classes.createModal}>
          <h2 className={classes.createTitle}>New Overlay Filter</h2>
          <p className={classes.createSubtitle}>
            Upload the animated WebP and name it — the filter is created as a
            draft you can then preview and publish.
          </p>
          <label className={classes.fieldLabel}>Name</label>
          <input
            className={classes.textInput}
            placeholder="Floating Hearts"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onBlur={e => setNewName(toTitleCase(e.target.value))}
          />
          <span className={classes.fieldHint}>
            Token: {derivedToken || '—'} — auto-generated, must start with a
            letter, frozen once published
          </span>
          <label className={classes.fieldLabel}>Tags</label>
          <input
            className={classes.textInput}
            placeholder="romantic, hearts (comma-separated)"
            value={newTags}
            onChange={e => setNewTags(e.target.value)}
          />
          <label className={classes.fieldLabel}>Mood</label>
          <input
            className={classes.textInput}
            placeholder="warm, dreamy (comma-separated)"
            value={newMood}
            onChange={e => setNewMood(e.target.value)}
          />
          <label className={classes.fieldLabel}>Media</label>
          <p className={classes.requirementsBlock}>{WEBP_REQUIREMENTS_COPY}</p>
          <label className={classes.uploadButton}>
            {newFile ? 'CHANGE FILE (.webp)' : 'CHOOSE FILE (.webp)'}
            <input
              type="file"
              accept="image/webp,.webp"
              onChange={handleFileChange}
              disabled={isCreating}
              style={{ display: 'none' }}
            />
          </label>
          {newFile && (
            <span className={classes.fieldHint}>
              Selected: {newFile.name}
              {finalizedMedia
                ? ` · processed ${finalizedMedia.width}×${finalizedMedia.height}`
                : ''}
            </span>
          )}
          {isCreating && (
            <div className={classes.inlineLoader}>
              <Loader isLoading={true} />
            </div>
          )}
          <div className={classes.createActions}>
            <ButtonOutline
              text={isCreating ? 'UPLOADING…' : 'CREATE DRAFT'}
              color="pink"
              disable={isCreating || !newFile}
              onClick={handleCreate}
            />
          </div>
        </div>
      </ModalWindow>

      <Wrapper scrollY>
        <StickyHeader className={classes.stickyHeader}>
          <Breadcrumbs
            items={[
              {
                text: 'Filters',
                onClick: () => {
                  navigate('/filters')
                },
              },
            ]}
          />
          <div className={classes.header}>
            <div className={classes.headerTitleWrapper}>
              <span className={classes.headerTitle}>Overlay Filters</span>
              <span className={classes.headerSubtitle}>
                Universal animated overlays. Publishing a filter makes it appear
                in the app with no app release.
              </span>
            </div>
            <div className={classes.headerButtons}>
              <ButtonOutline
                text="IMPORT ZIP"
                color="pinkBlack"
                fontFamily="Noto Sans"
                fontWeight={700}
                onClick={() => setIsImportOpen(true)}
              />
              <ButtonOutline
                text="+ NEW FILTER"
                color="pink"
                fontFamily="Noto Sans"
                fontWeight={700}
                onClick={() => setIsCreateOpen(true)}
              />
            </div>
          </div>
          <div className={classes.filterTabs}>
            {SHOW_OPTIONS.map(option => (
              <button
                key={option.value}
                className={`${classes.filterTab} ${
                  show === option.value ? classes.filterTabActive : ''
                }`}
                onClick={() => setShow(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </StickyHeader>

        {isLoading && (
          <div className={classes.loader}>
            <Loader isLoading={isLoading} />
          </div>
        )}

        {!isLoading && !isError && filters.length === 0 && (
          <div className={classes.emptyState}>
            <p>No filters yet.</p>
            <span>Create one to get started.</span>
          </div>
        )}

        {!isLoading && !isError && filters.length > 0 && (
          <div className={classes.grid}>
            {filters.map(filter => {
              const url = thumbnailUrl(filter)
              const status = statusLabel(filter)
              return (
                <div
                  key={filter.f_id}
                  className={classes.card}
                  onClick={() => setSelectedFilter(filter)}
                >
                  <div className={classes.cardThumb}>
                    {url ? (
                      <img
                        src={url}
                        alt={filter.name}
                        className={classes.cardThumbImg}
                      />
                    ) : (
                      <div className={classes.cardThumbEmpty}>No media</div>
                    )}
                  </div>
                  <div className={classes.cardBody}>
                    <div className={classes.cardName}>{filter.name}</div>
                    <div className={classes.cardToken}>{filter.token}</div>
                    <span
                      className={`${classes.statusChip} ${classes[`status_${status}`]}`}
                    >
                      {status}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Wrapper>

      {isError && (
        <h1 style={{ color: 'white', margin: 0 }}>
          {`Failed to fetch filters with code: ${error?.status ?? 'unknown'}`}
        </h1>
      )}
    </>
  )
}
